import React from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { StatusBadge } from "@/components/StatusBadge";
import { Linkedin, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/i18n/LanguageContext";

const initials = (name = "") => name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

export const LeadsTable = ({ leads, selected, onToggle, onToggleAll, onOpen, showStatus = true }) => {
  const t = useT();
  const allSelected = leads.length > 0 && selected.length === leads.length;
  return (
    <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
      <div className="overflow-x-auto">
        <Table data-testid="leads-results-table">
          <TableHeader>
            <TableRow className="bg-card/95">
              <TableHead className="w-10">
                <Checkbox checked={allSelected} onCheckedChange={onToggleAll} data-testid="leads-select-all-checkbox" aria-label="Select all" />
              </TableHead>
              <TableHead>{t("th_name")}</TableHead>
              <TableHead className="hidden md:table-cell">{t("th_title")}</TableHead>
              <TableHead>{t("th_company")}</TableHead>
              <TableHead className="hidden lg:table-cell">{t("th_location")}</TableHead>
              <TableHead className="hidden xl:table-cell">{t("th_subindustry")}</TableHead>
              {showStatus && <TableHead className="hidden sm:table-cell">{t("th_status")}</TableHead>}
              <TableHead className="hidden lg:table-cell">{t("th_email")}</TableHead>
              <TableHead className="text-right">{t("th_actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {leads.map((lead) => (
              <TableRow
                key={lead.id}
                data-testid="leads-results-row"
                className={`cursor-pointer hover:bg-secondary/60 ${lead.already_existed ? "bg-muted/40 opacity-70" : ""}`}
                onClick={() => onOpen(lead)}
              >
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selected.includes(lead.id)}
                    onCheckedChange={() => onToggle(lead.id)}
                    data-testid="leads-row-checkbox"
                    aria-label={`Select ${lead.full_name}`}
                  />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2.5">
                    <Avatar className="h-8 w-8"><AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">{initials(lead.full_name)}</AvatarFallback></Avatar>
                    <div>
                      <div className="flex items-center gap-1.5 font-medium leading-tight">
                        {lead.full_name}
                        {lead.already_existed && (
                          <span data-testid="already-seen-badge" className="rounded-full border border-slate-300 bg-slate-100 px-1.5 py-0 text-[10px] font-medium text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
                            {t("already_seen")}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground md:hidden">{lead.job_title}</div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{lead.job_title}</TableCell>
                <TableCell><span className="font-medium">{lead.company_name}</span><div className="text-xs text-muted-foreground">{lead.company_size}</div></TableCell>
                <TableCell className="hidden lg:table-cell text-sm">{lead.location}</TableCell>
                <TableCell className="hidden xl:table-cell"><Badge variant="outline" className="font-normal">{lead.sub_industry}</Badge></TableCell>
                {showStatus && <TableCell className="hidden sm:table-cell"><StatusBadge status={lead.status} /></TableCell>}
                <TableCell className="hidden lg:table-cell" onClick={(e) => e.stopPropagation()}>
                  <button
                    className="flex items-center gap-1 font-mono text-xs text-muted-foreground hover:text-foreground"
                    onClick={() => { navigator.clipboard.writeText(lead.email); toast.success(t("email_copied")); }}
                  >
                    <span className="max-w-[160px] truncate">{lead.email}</span>
                    <Copy className="h-3 w-3" />
                  </button>
                </TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-1">
                    <a href={lead.linkedin_url} target="_blank" rel="noreferrer">
                      <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="LinkedIn"><Linkedin className="h-4 w-4" /></Button>
                    </a>
                    <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="leads-open-detail-button" onClick={() => onOpen(lead)} aria-label="Open details"><ExternalLink className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
